import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { OPEN, Field, btn, sentForApproval } from "../../lib/ops";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const OK = { start: "Started", progress: "Progress saved", block: "Marked blocked", unblock: "Unblocked", done: "Marked done", qc_pass: "QC passed",
  qc_fail: "Sent back for rework", hold: "On hold", resume: "Resumed", cancel: "Step cancelled" };
const TITLE = { progress: "Update progress", block: "What's blocking it?", qc_fail: "Why did QC fail?", hold: "Why put it on hold?", cancel: "Why cancel this step?" };
const NEEDS_NOTE = ["qc_fail", "hold", "cancel"];
const PRIMARY = "bg-primary text-primary-foreground";

const BlockReasons = ({ reasons, value, onPick }) => (
  <div className="grid grid-cols-2 gap-2">{Object.entries(reasons).map(([k, l]) => (
    <button key={k} data-testid={`block-reason-${k}`} onClick={() => onPick(k)} className={`border p-3 text-sm text-left ${value === k ? "border-primary bg-primary/10" : ""}`}>{l}</button>))}</div>
);

const ActionDialog = ({ dlg, meta, onClose, onSend }) => {
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [progress, setProgress] = useState(50);
  const needNote = dlg.override || NEEDS_NOTE.includes(dlg.action);
  const ok = (!needNote || note.trim()) && (dlg.action !== "block" || dlg.override || reason);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-card" data-testid="task-action-dialog">
        <DialogHeader><DialogTitle>{dlg.override ? "Go ahead anyway?" : TITLE[dlg.action]}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {dlg.override && <p className="text-sm text-amber-700" data-testid="task-override-msg">{dlg.msg} Your reason is recorded on the UID.</p>}
          {dlg.action === "block" && !dlg.override && <BlockReasons reasons={meta.block_reasons} value={reason} onPick={setReason} />}
          {dlg.action === "progress" && !dlg.override && (
            <Field label={`Progress: ${progress}%`}><input type="range" min="0" max="95" step="5" value={progress} onChange={(e) => setProgress(Number(e.target.value))} className="w-full" data-testid="task-progress-input" /></Field>)}
          <textarea data-testid="task-action-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={needNote ? "Reason (required)" : "Note (optional)"} className="w-full bg-muted/60 border p-2 text-sm h-20" />
          <button data-testid="task-action-submit" disabled={!ok} onClick={() => onSend({ note, reason: reason || undefined, progress, override: !!dlg.override })} className={btn}>Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const workActions = (p, act, waiting) => {
  if (!act) return [];
  const s = p.status;
  return [
    s === "not_started" && !waiting && { a: "start", label: "Start", tone: PRIMARY },
    s === "wip" && { a: "done", label: "Done", tone: PRIMARY },
    s === "wip" && { a: "progress", label: "Update %", dlg: true },
    ["not_started", "wip"].includes(s) && { a: "block", label: "Blocked", dlg: true },
    s === "blocked" && { a: "unblock", label: "Unblock", tone: PRIMARY },
  ];
};

const QC_ACTIONS = [{ a: "qc_pass", label: "QC pass", tone: "bg-emerald-600 text-white" }, { a: "qc_fail", label: "QC fail", dlg: true }];
const CANCEL = { a: "cancel", label: "Cancel", tone: "border text-rose-600 hover:border-rose-400", dlg: true };

const holdActions = (p, plan, big) => {
  const s = p.status;
  if (s === "hold" && p.prepo_hold) return [{ a: "prepo" }];
  if (s === "hold") return plan ? [{ a: "resume", label: "Resume" }] : [];
  return plan && !big && ["not_started", "wip", "blocked"].includes(s) ? [{ a: "hold", label: "Hold", dlg: true }] : [];
};

const leadActions = (p, plan, mine, big) => [
  ...(!mine && p.status === "qc_pending" ? QC_ACTIONS : []),
  ...holdActions(p, plan, big),
  ...(plan && !big && OPEN.includes(p.status) ? [CANCEL] : []),
];

const actionsFor = (p, meta, mine, lead, big) => {
  const waiting = p.status === "not_started" && p.waiting_for?.length > 0 && !lead;
  return [waiting && { a: "wait" }, ...workActions(p, mine || lead, waiting), ...leadActions(p, meta.can.plan, mine, big)].filter(Boolean);
};

const ActBtn = ({ s, pid, size, onClick }) => {
  if (s.a === "wait") return <button disabled data-testid={`task-waiting-${pid}`} className={`border ${size} text-muted-foreground cursor-not-allowed`}>Waiting</button>;
  if (s.a === "prepo") return <span data-testid={`task-prepo-hold-${pid}`} className="text-[11px] text-amber-800 self-center">Waiting for Pre-PO approval</span>;
  return <button data-testid={`task-${s.a}-${pid}`} onClick={onClick} className={`${s.tone || "border hover:border-primary"} ${size} whitespace-nowrap`}>{s.label}</button>;
};

const useTaskSend = (p, lead, setDlg, onChanged) => (action, extra = {}) => api.post(`/ops/processes/${p.process_id}/action`, { action, version: p.version, ...extra })
  .then((r) => { setDlg(null); if (sentForApproval(r)) { onChanged?.(); return; } toast.success(OK[action]); onChanged?.(r.data); })
  .catch((e) => {
    const m = errMsg(e);
    if (e.response?.status === 409 && m.startsWith("Waiting for") && lead && !extra.override) { setDlg({ action, override: true, msg: m }); return; }
    toast.error(m);
    if (e.response?.status === 409) onChanged?.();
  });

export const TaskActions = ({ p, meta, onChanged, big = false }) => {
  const { user } = useAuth();
  const [dlg, setDlg] = useState(null);
  const lead = !!(meta && (meta.can.lead || meta.can.plan));
  const send = useTaskSend(p, lead, setDlg, onChanged);
  if (!meta) return null;
  const size = big ? "h-12 px-4 text-sm flex-1 min-w-[96px]" : "h-8 px-2.5 text-xs";
  const specs = actionsFor(p, meta, p.owner_id === user?.user_id, lead, big);
  return (
    <div className="flex flex-wrap gap-1.5">
      {specs.map((s) => <ActBtn key={s.a} s={s} pid={p.process_id} size={size} onClick={() => (s.dlg ? setDlg({ action: s.a }) : send(s.a))} />)}
      {dlg && <ActionDialog dlg={dlg} meta={meta} onClose={() => setDlg(null)} onSend={(x) => send(dlg.action, x)} />}
    </div>
  );
};
